import { Stack, styled, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { Chip, type ChipProps } from "../../chip/Chip";
import { IconFavorite } from "../../icon-favorite/IconFavorite";
import type { MetricGroup, MetricName } from "./metricCard.types";

// map metric: name - color
/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { METRIC_COLOR } from '@monthly/ui' */
export const METRIC_COLOR: Record<
  MetricGroup,
  { name: string; color: ChipProps["color"] } // se pueden tipar los colores con los que concuerdan con las props de color de Chip
> = {
  margin: { name: "Márgenes", color: "accent" },
  return: { name: "Retorno", color: "primary" },
  efficiency: { name: "Eficiencia", color: "yellowc" },
  liquidity: { name: "Liquidez", color: "greenc" },
  solvency: { name: "Solvencia", color: "redc" },
  management: { name: "Gestión", color: "pinkc" },
  valuation: { name: "Valuación", color: "violetc" },
  stability: { name: "Estabilidad", color: "orangec" },
  growth: { name: "Crecimiento", color: "secondary" },
};

const Container = styled(Stack)(({ theme, onClick }) => ({
  backgroundColor: theme.palette.common.white,
  cursor: onClick ? "pointer" : "default",
  display: "flex",
  width: "270px",
  alignItems: "center",
  position: "relative",
  boxShadow: theme.shadows[6],
  borderRadius: "12px",
  transition: "all 0.2s ease-out",
  "&:hover": {
    transform: onClick ? "translateY(-4px)" : "none",
  },
}));

// biome-ignore lint/style/useNamingConvention: We need a union here
type MetricCardVariant = "highlight" | "catalog";

interface CardIconProps {
  cardColor: ChipProps["color"];
}

const CardIcon = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "cardColor",
})<CardIconProps>(({ theme, cardColor = "primary" }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flex: "1 0 0",
  alignSelf: "stretch",
  borderRadius: "12px 0 0 12px",
  border: `1px solid ${theme.palette.divider}`,
  borderRight: "none",
  background: theme.palette[cardColor].gradient.background,
}));

const CardContent = styled(Stack)(({ theme }) => ({
  display: "flex",
  width: "172px",
  padding: "16px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  flexShrink: 0,
  alignSelf: "stretch",
  borderRadius: "0 12px 12px 0",
  border: `1px solid ${theme.palette.divider}`,
}));

const ValueContainer = styled(Stack)({
  alignItems: "center",
  flexDirection: "row",
});

interface MetricCardProps {
  metricGroup: MetricGroup;
  metricName: MetricName;
  change: number | null;
  value: number | null;
  valuePrefix?: string;
  valueSuffix?: string;
  icon: ReactNode;
  /**
   * The variant to use for styling.
   * @default highlight
   */
  variant?: MetricCardVariant;
  onFavoriteClick?: () => void;
  onClick?: () => void;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { MetricCard } from '@monthly/ui' */
export function MetricCard({
  metricGroup,
  metricName,
  change,
  value,
  valuePrefix,
  valueSuffix,
  icon,
  variant = "highlight",
  onFavoriteClick,
  onClick,
}: MetricCardProps) {
  const metricGroupLabel = METRIC_COLOR[metricGroup].name || "retorno";
  const metricColor = METRIC_COLOR[metricGroup].color || "secondary";
  const size = variant === "highlight" ? "72px" : "56px";
  return (
    <Container direction={"row"} onClick={onClick}>
      <CardIcon cardColor={metricColor}>
        <Stack width={size} height={size} alignItems={"center"}>
          {icon}
        </Stack>
      </CardIcon>
      <CardContent>
        {onFavoriteClick && (
          <Stack position="absolute" right="8px" top="8px">
            <IconFavorite
              onClick={(e) => {
                e.stopPropagation();
                onFavoriteClick();
              }}
            />
          </Stack>
        )}
        <Stack>
          <ValueContainer>
            <Typography
              variant="body1"
              component="span"
              sx={(theme) => ({
                color: theme.palette.secondary.main,
              })}
            >
              {valuePrefix}
            </Typography>
            <Typography
              variant="h2"
              sx={(theme) => ({
                color: theme.palette.secondary.dark,
              })}
            >
              {value === null ? "-" : value}
            </Typography>
            <Typography
              variant="body1"
              component="span"
              sx={(theme) => ({
                color: theme.palette.secondary.main,
              })}
            >
              {valueSuffix}
            </Typography>
          </ValueContainer>
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.secondary.dark,
              fontWeight: theme.typography.fontWeightSemiBold,
              whiteSpace: "nowrap",
            })}
          >
            {metricName}
          </Typography>
        </Stack>
        <Stack direction={"row"} gap={0.5}>
          {variant === "highlight" && (
            <Chip label={metricGroupLabel} size="small" color={metricColor} />
          )}
          <Chip
            label={
              change === null || change === undefined
                ? "-"
                : change > 0
                  ? `+${change}%`
                  : `${change}%`
            }
            size="small"
            color="secondary"
          />
        </Stack>
      </CardContent>
    </Container>
  );
}
